import React, {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import loadScript from '@tv2/lib-core/loadScript';

interface Cookiebot {
  hasResponse: boolean;
  bulkconsent?: {
    expireMonths: number;
    marketing: boolean;
    preferences: boolean;
    statistics: boolean;
    ticket: string;
    utc: number;
  };
  consent?: {
    marketing: boolean;
    necessary: boolean;
    preferences: boolean;
    statistics: boolean;
    stamp: string;
  };
}

export type CookiebotWindow = Window & {
  Cookiebot?: Cookiebot;
};

declare let window: CookiebotWindow;

type ConsentStatus = 'unknown' | 'no' | 'yes';

type CMPContextType = {
  consent: ConsentStatus;
  hasResponse: boolean;
  cookieBot: any | undefined;
  getCookieBot: () => any | undefined;
};

const CMPContext = createContext<CMPContextType>({
  consent: 'unknown',
  hasResponse: false,
  cookieBot: null,
  getCookieBot: () => null,
});

CMPContext.displayName = 'CMP';

export function useConsent() {
  const context = useContext(CMPContext);
  return context;
}

export const CMPProvider: FunctionComponent<PropsWithChildren<{ scriptUrl: string }>> = ({
  children,
  scriptUrl,
}) => {
  const [consent, setConsent] = useState<ConsentStatus>('unknown');

  useEffect(() => {
    // If the blocking functionality of CookieBot is not used, then it should be possible to also
    // load this script conditionally based on if the CookieConsent cookie has been set. For now
    // it is just loaded every time.
    if (scriptUrl) loadScript(scriptUrl);
  }, [scriptUrl]);

  useEffect(() => {
    getCurrentConsent().then(hasConsent => {
      setConsent(hasConsent ? 'yes' : 'no');
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      consent,
      hasResponse: consent !== 'unknown',
      cookieBot: typeof window !== 'undefined' ? window.Cookiebot : undefined,
      getCookieBot: () => (typeof window !== 'undefined' ? window.Cookiebot : undefined),
    }),
    [consent]
  );

  return <CMPContext.Provider value={contextValue}>{children}</CMPContext.Provider>;
};

let currentConsentPromise: Promise<boolean>;
export function getCurrentConsent(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (!currentConsentPromise)
    currentConsentPromise = new Promise(res => {
      if (window.Cookiebot?.hasResponse) {
        res(readCookiebotConsent());
        return;
      }
      const cookieConsent = loadConsentFromCookie();
      if (cookieConsent !== null) {
        res(cookieConsent);
        return;
      }
      const readyEvent = 'CookiebotOnConsentReady';
      const callback = () => {
        res(readCookiebotConsent());
      };
      setTimeout(() => {
        window.removeEventListener(readyEvent, callback);
        res(false);
      }, 500);
      window.addEventListener(readyEvent, callback, false);
    });
  return currentConsentPromise;
}

function loadConsentFromCookie() {
  const consentCookie = getCookie('CookieConsent');
  if (!consentCookie) return null;
  if (consentCookie === '-1') return true;
  try {
    const val = JSON.parse(
      decodeURIComponent(consentCookie)
        .replace(/(\w+):+/g, '"$1":')
        .replace(/'/g, '"')
    );
    return readConsent(val);
  } catch {
    return null;
  }
}

function readConsent(consent?: Cookiebot['consent'] | Cookiebot['bulkconsent']) {
  if (!consent) return false;
  const { marketing, preferences, statistics } = consent;
  const hasConsent = marketing && preferences && statistics;
  return hasConsent;
}

function readCookiebotConsent() {
  return readConsent(window.Cookiebot?.consent) || readConsent(window.Cookiebot?.bulkconsent);
}

export function getCookie(cookieName: string) {
  if (typeof window === 'undefined') return null;
  const cookies = document.cookie;
  const cookie = cookies.split('; ').find((c: string) => c.startsWith(cookieName));
  if (!cookie) return null;
  const [_, ...value] = cookie.split('=');
  return value.join('=');
}
